// pages/Settings.js - User Settings Page (MVP Phase 1)

import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Form, Button, Alert, Nav, Badge } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import DesktopLayout from '../layouts/DesktopLayout';
import { secureApi } from '../services/secureApi';
import { formatDate } from '../utils/dateFormatter';
import PhoneInput from '../components/PhoneInput';
import NotificationSettingsPanel from '../components/NotificationSettingsPanel';

const Settings = () => {
  const { user, logout } = useContext(AuthContext);

  // Tab state
  const [activeTab, setActiveTab] = useState('profile');

  // Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    nationalId: '',
    role: '',
    status: '',
    timezone: 'Asia/Riyadh',
    locale: 'ar',
    lastLogin: null
  });
  const [profileLoading, setProfileLoading] = useState(false);
  // Save stays locked until the profile has actually loaded: after a failed read the form is blank,
  // and saving it would overwrite the account with empty values (tasks/owner-screens-mobile.md step 7).
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Preferences state - removed for Phase 1 (language not supported yet)
  // Will be added back in Phase 2 when English support is ready

  // Load user profile on mount
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await secureApi.get('/api/v1/users/me');
      const userData = response.data.user;

      setProfileData({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        nationalId: userData.nationalId || '',
        role: userData.role || '',
        status: userData.status || '',
        timezone: userData.timezone || 'Asia/Riyadh',
        locale: userData.locale || 'ar',
        lastLogin: userData.lastLogin
      });
      setProfileLoaded(true);

      // Preferences removed - no language toggle in Phase 1

    } catch (error) {
      console.error('Error loading profile:', error);
      setProfileError('فشل تحميل البيانات');
    }
  };

  // Profile update handler
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const updates = {
        name: profileData.name,
        phone: profileData.phone
      };

      await secureApi.put('/api/v1/users/profile', updates);

      setProfileSuccess('✅ تم تحديث الملف الشخصي بنجاح');

      // Clear success message after 3 seconds
      setTimeout(() => setProfileSuccess(''), 3000);

    } catch (error) {
      console.error('Profile update error:', error);
      setProfileError(error.response?.data?.message || 'فشل تحديث الملف الشخصي');
    } finally {
      setProfileLoading(false);
    }
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('كلمات المرور الجديدة غير متطابقة');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      setPasswordLoading(false);
      return;
    }

    try {
      await secureApi.post('/api/v1/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordSuccess('✅ تم تغيير كلمة المرور بنجاح. سيتم تسجيل خروجك خلال 3 ثوان...');

      // Clear form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Logout after 3 seconds (tokens invalidated)
      setTimeout(() => {
        logout();
      }, 3000);

    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError(error.response?.data?.message || 'فشل تغيير كلمة المرور');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Preferences removed for Phase 1

  // Role badge styling
  const getRoleBadge = (role) => {
    const roleMap = {
      admin: { text: 'مشرف', variant: 'danger' },
      'ops-manager': { text: 'مدير عمليات', variant: 'warning' },
      ops: { text: 'عمليات', variant: 'warning' },
      'maintenance-supervisor': { text: 'مشرف صيانة', variant: 'info' },
      maintenance: { text: 'صيانة', variant: 'info' },
      turnKeyOwner: { text: 'مالك - خدمة كاملة', variant: 'success' },
      accountant: { text: 'محاسب', variant: 'secondary' }
    };
    const config = roleMap[role] || { text: role, variant: 'secondary' };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  // Status badge styling
  const getStatusBadge = (status) => {
    const statusMap = {
      invited: { text: 'مدعو', variant: 'secondary' },
      active: { text: 'نشط', variant: 'success' },
      suspended: { text: 'موقوف', variant: 'danger' },
      archived: { text: 'مؤرشف', variant: 'dark' }
    };
    const config = statusMap[status] || { text: status, variant: 'secondary' };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  // Render Profile Tab
  const renderProfileTab = () => (
    <Form onSubmit={handleProfileUpdate} dir="rtl">
      {profileError && (
        <Alert variant="danger" dismissible onClose={() => setProfileError('')}>
          {profileError}
        </Alert>
      )}

      {profileSuccess && (
        <Alert variant="success" dismissible onClose={() => setProfileSuccess('')}>
          {profileSuccess}
        </Alert>
      )}

      <Form.Group className="mb-3">
        <Form.Label>الاسم *</Form.Label>
        <Form.Control
          type="text"
          required
          className="touch-input"
          value={profileData.name}
          onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
          placeholder="الاسم الكامل"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>البريد الإلكتروني</Form.Label>
        <Form.Control
          type="email"
          value={profileData.email}
          disabled
          className="bg-light touch-input"
        />
        <Form.Text className="text-muted">
          لتغيير البريد الإلكتروني، يرجى التواصل مع الدعم
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>رقم الجوال</Form.Label>
        <PhoneInput
          value={profileData.phone}
          defaultCountry="SA"
          onChange={(phone) => setProfileData({ ...profileData, phone })}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>العضوية</Form.Label>
        <div>{getRoleBadge(profileData.role)}</div>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>حالة الحساب</Form.Label>
        <div>{getStatusBadge(profileData.status)}</div>
      </Form.Group>

      <Button
        variant="primary"
        type="submit"
        className="touch-btn"
        disabled={profileLoading || !profileLoaded}
      >
        {profileLoading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
      </Button>
    </Form>
  );

  // Render Security Tab
  const renderSecurityTab = () => (
    <div dir="rtl">
      {/* Last Login Info */}
      <Card className="mb-4" bg="light">
        <Card.Body>
          <h6 className="mb-2">آخر تسجيل دخول</h6>
          <p className="text-muted mb-0">
            {profileData.lastLogin ? formatDate(profileData.lastLogin) : 'لا توجد بيانات'}
          </p>
        </Card.Body>
      </Card>

      {/* Change Password Form */}
      <h5 className="mb-3">تغيير كلمة المرور</h5>

      {passwordError && (
        <Alert variant="danger" dismissible onClose={() => setPasswordError('')}>
          {passwordError}
        </Alert>
      )}

      {passwordSuccess && (
        <Alert variant="success" dismissible onClose={() => setPasswordSuccess('')}>
          {passwordSuccess}
        </Alert>
      )}

      <Form onSubmit={handlePasswordChange}>
        <Form.Group className="mb-3">
          <Form.Label>كلمة المرور الحالية *</Form.Label>
          <Form.Control
            type="password"
            required
            className="touch-input"
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
            placeholder="أدخل كلمة المرور الحالية"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>كلمة المرور الجديدة *</Form.Label>
          <Form.Control
            type="password"
            required
            className="touch-input"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
            placeholder="أدخل كلمة المرور الجديدة"
            minLength={6}
          />
          <Form.Text className="text-muted">
            يجب أن تكون 6 أحرف على الأقل
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>تأكيد كلمة المرور الجديدة *</Form.Label>
          <Form.Control
            type="password"
            required
            className="touch-input"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
            placeholder="أعد إدخال كلمة المرور الجديدة"
          />
        </Form.Group>

        <Button
          variant="danger"
          type="submit"
          className="touch-btn"
          disabled={passwordLoading}
        >
          {passwordLoading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
        </Button>

        <Alert variant="warning" className="mt-3 mb-0">
          <small>⚠️ سيتم تسجيل خروجك من جميع الأجهزة بعد تغيير كلمة المرور</small>
        </Alert>
      </Form>
    </div>
  );

  // Render Preferences Tab
  //
  // Notifications lead, and the timezone/language facts follow. Those two are static information
  // nobody returns to this tab for; the notification panel is the only thing here anyone acts on —
  // and it is where an operator comes when their phone has gone quiet (D87).
  const renderPreferencesTab = () => (
    <div dir="rtl">
      <NotificationSettingsPanel />

      <Alert variant="info">
        <h6 className="mb-2">التفضيلات العامة</h6>
        <p className="mb-2">
          <strong>المنطقة الزمنية:</strong> الرياض (Asia/Riyadh)
        </p>
        <p className="mb-0">
          <strong>اللغة:</strong> العربية
        </p>
      </Alert>
    </div>
  );

  return (
    <DesktopLayout>
      <Container fluid className="settings-page p-4" dir="rtl">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>الإعدادات</h2>
        </div>

        <Card>
          <Card.Header style={{ direction: 'rtl' }}>
            <Nav variant="tabs" activeKey={activeTab} onSelect={(key) => setActiveTab(key)} className="justify-content-start">
              <Nav.Item>
                <Nav.Link eventKey="profile">الملف الشخصي</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="security">الأمان</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="preferences">التفضيلات</Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>

          <Card.Body className="p-4" style={{ textAlign: 'right' }}>
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'security' && renderSecurityTab()}
            {activeTab === 'preferences' && renderPreferencesTab()}
          </Card.Body>
        </Card>
      </Container>
    </DesktopLayout>
  );
};

export default Settings;
